import React, { Component } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ajax_wrapper, ajax_wrapper_file } from 'functions';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// HOC to inject URL parameters and navigation
const withRouterHooks = (WrappedComponent) => (props) => {
    const params = useParams();
    const navigate = useNavigate();
    return <WrappedComponent {...props} params={params} navigate={navigate} />;
};

class ArticleAdminFormPage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            articleData: {
                title: '',
                content: '',
                author: null, // Will store author ID
                status: 'draft',
                active: true, // Default to true for new articles
                publication_date: '',
                seo_meta_title: '',
                seo_meta_description: '',
                featured_image: null, // For new image file
            },
            existingImageUrl: null, // To display current image if editing
            allUsers: [], // For author selection
            loading: true,
            error: null,
            isEditMode: false,
            articleSlug: null,
        };
        this.handleChange = this.handleChange.bind(this);
        this.handleFileChange = this.handleFileChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.loadInitialData = this.loadInitialData.bind(this);
        this.handleContentChange = this.handleContentChange.bind(this);
    }

    componentDidMount() {
        const { slug } = this.props.params;
        const isEditMode = !!slug;
        this.setState({ isEditMode, articleSlug: slug }, () => {
            this.loadInitialData();
        });
    }

    loadInitialData() {
        this.setState({ loading: true });
        const { isEditMode, articleSlug } = this.state;

        this.fetchUsers();

        if (isEditMode && articleSlug) {
            const articleRequest = ajax_wrapper('GET', `/api/blog/admin/articles/${articleSlug}/`, {}, (res) => {
                if (res && res.slug) {
                    this.setState(prevState => ({
                        articleData: {
                            ...prevState.articleData,
                            title: res.title || '',
                            content: res.content || '',
                            author: res.author,
                            status: res.status || 'draft',
                            active: res.active === undefined ? true : res.active,
                            publication_date: res.publication_date ? res.publication_date.split('T')[0] : '',
                            seo_meta_title: res.seo_meta_title || '',
                            seo_meta_description: res.seo_meta_description || '',
                        },
                        existingImageUrl: res.featured_image || null,
                    }));
                } else {
                    this.setState({ error: 'Failed to load article for editing.'});
                }
            });
            Promise.all([this.fetchUsers(), articleRequest])
                .then(() => this.setState({ loading: false }))
                .catch(() => this.setState({ error: 'Error loading initial data.', loading: false }));
        } else {
            Promise.all([
            ]).then(() => this.setState({ loading: false }))
              .catch(() => this.setState({ error: 'Error loading user data.', loading: false }));
            this.setState({ loading: false });
        }
    }

    fetchUsers() {
        ajax_wrapper('GET', '/user/get_all_users/', {}, (res) => {
            if (res && res.users && Array.isArray(res.users)) {
                const staffUsers = res.users.filter(user => user.is_staff);
                this.setState({ allUsers: staffUsers });
            } else {
                console.error("Failed to load or parse users for author selection, or no staff users found.", res);
                this.setState({ allUsers: [] });
            }
        });
    }

    handleChange(event) {
        const { name, value, type, multiple } = event.target;
        const finalValue = type === 'checkbox' ? event.target.checked : value;
        this.setState(prevState => ({
            articleData: { ...prevState.articleData, [name]: finalValue }
        }));
    }

    handleContentChange(content) {
        this.setState(prevState => ({
            articleData: { ...prevState.articleData, content: content }
        }));
    }

    handleFileChange(event) {
        this.setState(prevState => ({
            articleData: { ...prevState.articleData, featured_image: event.target.files[0] }
        }));
    }

    handleSubmit(event) {
        event.preventDefault();
        this.setState({ loading: true, error: null });
        const { articleData, isEditMode, articleSlug } = this.state;

        const formData = new FormData();
        Object.keys(articleData).forEach(key => {
            if (articleData[key] !== null && articleData[key] !== undefined) {
                formData.append(key, articleData[key]);
            }
        });

        if (articleData.author === null) {
            formData.delete('author');
        }

        const url = isEditMode ? `/api/blog/admin/articles/${articleSlug}/` : '/api/blog/admin/articles/';
        const method = isEditMode ? 'PATCH' : 'POST';

        ajax_wrapper_file(method, url, formData, (response, xhr) => {
            if (response && (response.slug || response.id || (Array.isArray(response) && response.length > 0) || (typeof response === 'object' && Object.keys(response).length > 0 && !response.error))) {
                this.setState({ loading: false });
                this.props.navigate('/admin/articles');
            } else {
                let errorMsg = 'Operation failed.';
                if (response && response.error) {
                    errorMsg = response.error;
                } else if (xhr && xhr.responseText) {
                    try {
                        const errRes = JSON.parse(xhr.responseText);
                        errorMsg = Object.values(errRes).join(' ');
                    } catch (e) {
                        errorMsg = xhr.statusText || 'An unknown error occurred.';
                    }
                } else if (typeof response === 'string') {
                    try {
                        const errRes = JSON.parse(response);
                        errorMsg = Object.values(errRes).join(' ');
                    } catch (e) {
                        errorMsg = response || 'An unknown error occurred.';
                    }
                }
                this.setState({ error: errorMsg, loading: false });
                alert(`Error: ${errorMsg}`);
            }
        });
    }

    render() {
        const { articleData, allUsers, loading, error, isEditMode, existingImageUrl } = this.state;

        const getAuthorDisplayName = (user) => {
            const email = user.email;
            switch (email) {
                case 'andrew@yourhomebase.co':
                    return 'Andrew';
                case 'ian@yourhomebase.co':
                    return 'Ian';
                case 'jeremy@yourhomebase.co':
                    return 'Jeremy';
                case 'dmiller89@gmail.com':
                    return 'Dave Miller'; // Display as blank as requested
                default:
                    // Fallback for other emails: extract and capitalize name part
                    const namePart = email.split('@')[0];
                    if (namePart) {
                        return namePart.charAt(0).toUpperCase() + namePart.slice(1);
                    }
                    return email; // Absolute fallback to full email if split fails
            }
        };

        const formStyle = { padding: '20px', maxWidth: '800px', margin: '0 auto' };
        const inputGroupStyle = { marginBottom: '15px' };
        const labelStyle = { display: 'block', marginBottom: '5px', fontWeight: 'bold' };
        const inputStyle = { width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' };
        const textAreaStyle = { ...inputStyle, minHeight: '200px' };
        const selectStyle = { ...inputStyle };
        const buttonStyle = { padding: '10px 15px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
        const imagePreviewStyle = { maxWidth: '200px', maxHeight: '200px', marginTop: '10px', display: 'block' };

        if (loading) return <div style={formStyle}><p>Loading form...</p></div>;
        if (error && !isEditMode) return <div style={formStyle}><p style={{color: 'red'}}>Error loading form data: {error}</p></div>;

        return (
            <div style={formStyle}>
                <h1>{isEditMode ? 'Edit Article' : 'Create New Article'}</h1>
                {error && <p style={{color: 'red'}}>Error: {error}</p>}
                <form onSubmit={this.handleSubmit}>
                    <div style={inputGroupStyle}>
                        <label htmlFor="title" style={labelStyle}>Title:</label>
                        <input type="text" name="title" id="title" value={articleData.title} onChange={this.handleChange} style={inputStyle} required />
                    </div>

                    <div style={inputGroupStyle}>
                        <label htmlFor="content" style={labelStyle}>Content:</label>
                        <ReactQuill
                            theme="snow"
                            value={articleData.content}
                            onChange={this.handleContentChange}
                            style={{ minHeight: '300px', backgroundColor: 'white' }}
                            modules={ArticleAdminFormPage.modules}
                            formats={ArticleAdminFormPage.formats}
                        />
                    </div>

                    <div style={inputGroupStyle}>
                        <label htmlFor="author" style={labelStyle}>Author:</label>
                        <select name="author" id="author" value={articleData.author || ''} onChange={this.handleChange} style={selectStyle}>
                            <option value="">Select Author (Optional)</option>
                            {allUsers.map(user => (
                                <option key={user.id} value={user.id}>{getAuthorDisplayName(user)}</option>
                            ))}
                        </select>
                    </div>

                    <div style={inputGroupStyle}>
                        <label htmlFor="status" style={labelStyle}>Status:</label>
                        <select name="status" id="status" value={articleData.status} onChange={this.handleChange} style={selectStyle}>
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                        </select>
                    </div>

                    <div style={inputGroupStyle}>
                        <label htmlFor="publication_date" style={labelStyle}>Publication Date:</label>
                        <input type="date" name="publication_date" id="publication_date" value={articleData.publication_date} onChange={this.handleChange} style={inputStyle} />
                    </div>

                    <div style={inputGroupStyle}>
                        <label htmlFor="featured_image" style={labelStyle}>Featured Image:</label>
                        {isEditMode && existingImageUrl && (
                            <div>
                                <p>Current image:</p>
                                <img src={existingImageUrl} alt="Current featured" style={imagePreviewStyle} />
                                <p>Upload a new image below to replace it.</p>
                            </div>
                        )}
                        <input type="file" name="featured_image_file" id="featured_image_file" onChange={this.handleFileChange} style={inputStyle} accept="image/*" />
                    </div>

                    <div style={inputGroupStyle}>
                        <label htmlFor="seo_meta_title" style={labelStyle}>SEO Meta Title:</label>
                        <input type="text" name="seo_meta_title" id="seo_meta_title" value={articleData.seo_meta_title} onChange={this.handleChange} style={inputStyle} />
                    </div>

                    <div style={inputGroupStyle}>
                        <label htmlFor="seo_meta_description" style={labelStyle}>SEO Meta Description:</label>
                        <textarea name="seo_meta_description" id="seo_meta_description" value={articleData.seo_meta_description} onChange={this.handleChange} style={inputStyle} />
                    </div>

                    <div style={inputGroupStyle}>
                        <label htmlFor="active" style={{...labelStyle, display: 'inline-block', marginRight: '10px'}}>Active (visible on public site):</label>
                        <input 
                            type="checkbox" 
                            name="active" 
                            id="active" 
                            checked={articleData.active} 
                            onChange={this.handleChange} 
                            style={{verticalAlign: 'middle'}}
                        />
                    </div>

                    <button type="submit" style={buttonStyle} disabled={loading}>
                        {loading ? 'Saving...' : (isEditMode ? 'Update Article' : 'Create Article')}
                    </button>
                    <Link to="/admin/articles" style={{ marginLeft: '10px', textDecoration:'none', color:'#007bff' }}>Cancel</Link>
                </form>
            </div>
        );
    }
}

ArticleAdminFormPage.modules = {
    toolbar: [
        [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
        [{size: []}],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'},
         {'indent': '-1'}, {'indent': '+1'}],
        ['link', 'image', 'video'],
        ['clean'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
    ],
    clipboard: {
      matchVisual: false,
    }
};

ArticleAdminFormPage.formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'video',
    'color', 'background',
    'align',
];

export default withRouterHooks(ArticleAdminFormPage); 